/**
 * field-rule-validation.ts — walidacja REGUŁ pola z kreatora (min/max/wzorzec/długość/
 * daty z `validationRules`), STEROWANA TYPEM pola. Oś komplementarna do
 * `field-format-validation.ts` (ta pilnuje wrodzonego formatu typu: email/url/liczba/data;
 * ta — konfigurowalnych reguł z katalogu).
 *
 * WSPÓLNA dla wszystkich stacków: moduły własne (F12, przez `FieldRegistry.validateRules`),
 * kartoteki systemowe i 7 klas dokumentów (silnik formatek, przez `buildEngineRuleChecks`).
 * Odpowiednik serwerowego `apps/api/lib/validators/field-rules.ts` — te same reguły,
 * te same komunikaty (`common.validationIssue.*`), spójnie klient↔serwer.
 *
 * Kontrakt: zwraca STABILNY kod + parametry (nie gotowy tekst) — tłumaczenie robi
 * callsite przez `resolveRuleErrorMessage`. Pustą wartość przepuszcza (obecność =
 * osobna reguła „wymagane").
 */

import type { TFunction } from 'i18next';

export type FieldRuleErrorCode =
  | 'tooShort'
  | 'tooLong'
  | 'tooSmallNumber'
  | 'tooBigNumber'
  | 'invalidFormat'
  | 'dateTooEarly'
  | 'dateTooLate';

export interface FieldRuleError {
  code: FieldRuleErrorCode;
  params?: Record<string, string | number>;
}

/**
 * Typy pól o wartości liczbowej — słownik modułów custom
 * (number/decimal/currency/percent/rating/slider) ORAZ systemowych (integer/numeric).
 */
const NUMBER_FIELD_TYPES = new Set([
  'number',
  'decimal',
  'currency',
  'percent',
  'rating',
  'slider',
  'integer',
  'numeric',
]);
/** Typy pól o wartości daty — custom (date/datetime) + systemowe (date/timestamp). */
const DATE_FIELD_TYPES = new Set(['date', 'datetime', 'timestamp']);

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/**
 * Waliduje wartość względem reguł pola. Zwraca `FieldRuleError` (kod + parametry)
 * albo `null` (poprawna/pusta). Niepoprawny wzorzec z kreatora nie blokuje zapisu.
 */
export function validateFieldRules(
  fieldType: string,
  rules: Record<string, string> | undefined,
  value: unknown,
): FieldRuleError | null {
  if (!rules || isEmpty(value)) return null;

  if (NUMBER_FIELD_TYPES.has(fieldType)) {
    const num = typeof value === 'number' ? value : Number(String(value).trim().replace(',', '.'));
    if (Number.isNaN(num)) return null; // format liczby pilnuje oś format/serwer
    if (rules.min != null && num < Number(rules.min))
      return { code: 'tooSmallNumber', params: { min: rules.min } };
    if (rules.max != null && num > Number(rules.max))
      return { code: 'tooBigNumber', params: { max: rules.max } };
    return null;
  }

  if (DATE_FIELD_TYPES.has(fieldType)) {
    const str = String(value);
    // Porównanie ISO (YYYY-MM-DD) leksykograficznie = chronologicznie.
    if (rules.min_date && str < rules.min_date)
      return { code: 'dateTooEarly', params: { date: rules.min_date } };
    if (rules.max_date && str > rules.max_date)
      return { code: 'dateTooLate', params: { date: rules.max_date } };
    return null;
  }

  // Pozostałe = wartość tekstowa: długość + wzorzec.
  const str = String(value);
  if (rules.min_length != null && str.length < Number(rules.min_length))
    return { code: 'tooShort', params: { min: rules.min_length } };
  if (rules.max_length != null && str.length > Number(rules.max_length))
    return { code: 'tooLong', params: { max: rules.max_length } };
  if (rules.pattern) {
    try {
      if (!new RegExp(rules.pattern).test(str)) return { code: 'invalidFormat' };
    } catch {
      // niepoprawny wzorzec z kreatora → nie blokuj
    }
  }
  return null;
}

/** Rozwiązuje `FieldRuleError` na polski komunikat (wspólne klucze `common.validationIssue.*`). */
export function resolveRuleErrorMessage(err: FieldRuleError, t: TFunction): string {
  switch (err.code) {
    case 'tooShort':
      return t('common.validationIssue.tooSmallString', { min: err.params?.min });
    case 'tooLong':
      return t('common.validationIssue.tooBigString', { max: err.params?.max });
    case 'tooSmallNumber':
      return t('common.validationIssue.tooSmallNumber', { min: err.params?.min });
    case 'tooBigNumber':
      return t('common.validationIssue.tooBigNumber', { max: err.params?.max });
    case 'invalidFormat':
      return t('common.validationIssue.invalidFormat');
    case 'dateTooEarly':
      return t('common.validationIssue.dateTooEarly', { date: err.params?.date });
    case 'dateTooLate':
      return t('common.validationIssue.dateTooLate', { date: err.params?.date });
  }
}

/** Pojedyncze sprawdzenie reguł: klucz błędu, typ pola, reguły i wartość. */
export interface FieldRuleCheck {
  /** Klucz, pod którym błąd trafi do `useFieldErrors` (core: stateKey, ext: slug). */
  key: string;
  fieldType: string;
  rules?: Record<string, string>;
  value: unknown;
}

/**
 * Uruchamia walidację reguł dla listy pól, zwraca mapę `klucz → FieldRuleError` dla
 * tych, które NIE przeszły. Silnik-agnostyczny — callsite buduje `checks`
 * (np. `buildEngineRuleChecks` z configu silnika).
 */
export function collectFieldRuleErrors(checks: FieldRuleCheck[]): Record<string, FieldRuleError> {
  const out: Record<string, FieldRuleError> = {};
  for (const { key, fieldType, rules, value } of checks) {
    const err = validateFieldRules(fieldType, rules, value);
    if (err) out[key] = err;
  }
  return out;
}
